import dayjs from 'dayjs';
import Big from 'big.js';
import { useState, useEffect, useCallback } from 'react';
import { formatCreatedAt } from 'helpers/dates';
import { fetchTrades } from '../annualReport/trades';

export const formatAmount = (amount, decimalPrecision) => {
  try {
    return amount && new Big(amount).round(decimalPrecision ?? 18, Big.roundDown).toFixed();
  } catch (e) {
    console.error(`cannot format amount: ${amount} with decimal precision ${decimalPrecision}`);
    throw e;
  }
};

export const formatDate = date => date.toISOString();

const sortTrades = (trade1, trade2) =>
  // eslint-disable-next-line no-nested-ternary
  trade1.created.isAfter(trade2.created) ? -1 : trade2.created.isAfter(trade1.created) ? 1 : 0;

export const formatRow = assetsData => (row, index) => ({
  key: `${row.id}-${index}`,
  orderId: row.id,
  pair: row.notes?.replace('-', '/'),
  side: row.side,
  isBuy: row.isBuy,
  filledAt: formatCreatedAt(row.created),
  settledFee: `${formatAmount(row.fee, assetsData[row.quoteCurrency]?.decimalPrecisionDisplay)} ${
    row.quoteCurrency
  }`,
  settledPrice: `${formatAmount(row.price, assetsData[row.quoteCurrency]?.decimalPrecisionDisplay)} ${
    row.quoteCurrency
  }`,
  settledQty: `${formatAmount(row.quantity, assetsData[row.baseCurrency]?.decimalPrecisionDisplay)} ${
    row.baseCurrency
  }`,
  settledTotal: `${formatAmount(row.fiatAmount, assetsData[row.quoteCurrency]?.decimalPrecisionDisplay)} ${
    row.quoteCurrency
  }`,
  created: row.created,
  quoteCurrency: row.quoteCurrency,
  baseCurrency: row.baseCurrency
});

export const useTradeActivity = ({ skip = false, accountId, startTime, endTime, asset, assetsData }) => {
  const [data, setData] = useState();

  const refetch = useCallback(() => {
    setResult(res => ({ ...res, loading: true, data: null, error: null }));
    fetchTrades(accountId, startTime ? dayjs(startTime) : null, endTime ? dayjs(endTime) : null)
      .then(trades => setData(trades.map(formatRow(assetsData))))
      .catch(e => setResult(res => ({ ...res, loading: false, data: null, error: e })));
  }, [accountId, assetsData, endTime, startTime]);

  const [result, setResult] = useState({ loading: false, data: undefined, error: undefined, refetch });

  useEffect(() => {
    if (skip || !assetsData || Object.keys(assetsData).length === 0) return;
    refetch();
  }, [assetsData, refetch, skip]);

  useEffect(() => {
    if (data) {
      setResult(res => ({
        ...res,
        loading: false,
        data: data
          .filter(trade => asset === 'All' || trade.quoteCurrency === asset || trade.baseCurrency === asset)
          .sort(sortTrades),
        error: null
      }));
    }
  }, [data, asset]);

  return result;
};
